import type { IntlShape } from 'react-intl';
import { useIntl, defineMessages } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { get, useForm } from 'react-hook-form';
import { Grid, Select, TextField, useDeviceType } from '@packages/shared';
import type {
  GTMEventGlycerinDisplayError,
  GTMEventGlycerinDisplayForm,
  GTMEventGlycerinFormSubmitClick,
} from '@packages/tracking';
import { GlycerinErrorSeverity, useTracking } from '@packages/tracking';
import config from '@packages/config';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import type { ObjectSchema, SchemaDescription } from 'yup';
import type { CustomerDataWithStatus } from '@packages/utilities';
import { useCustomer } from '@packages/utilities';
import { createFormSubSelectionsSelect } from '../helpers/createFormElements';
import { InputIDs } from '../types';
import { formMessages, specialErrorMessages } from '../messages';
import { SuccessPage } from '../SuccessPage';
import { noSpecChars, noSpecCharsNoNumbers, numbersOnly } from '../helpers/validationSchema';
import type { ConvertedFormMessageDataType } from '../GeneralForm/helpers';
import { submitMessage } from '../GeneralForm/helpers';
import { GeneralFormWrapper } from '../GeneralForm';
import { useFormDeviceType } from '../GeneralForm/helpers/useFormDeviceType';

type SetErrorType = `root.${InputId}` | 'root';
type InputId =
  | 'subject'
  | 'message'
  | 'firstname'
  | 'lastname'
  | 'street'
  | 'housenumber'
  | 'city'
  | 'postalCode'
  | 'email'
  | 'phoneNumber'
  | 'customerid';
type FormValues = {
  [InputIDs.MESSAGE]?: string;
  [InputIDs.SUBJECT]?: string;
  [InputIDs.FIRSTNAME]: string;
  [InputIDs.LASTNAME]: string;
  [InputIDs.STREET]: string;
  [InputIDs.HOUSENUMBER]: string;
  [InputIDs.CITY]: string;
  [InputIDs.POSTALCODE]: string;
  [InputIDs.EMAIL]: string;
  [InputIDs.PHONENUMBER]?: string;
  [InputIDs.CUSTOMERID]?: string;
};
const contactMessages = defineMessages({
  headline: {
    id: 'forms.contact.headline',
    defaultMessage: 'Wie können wir dir helfen?',
  },
  description: {
    id: 'forms.contact.description',
    defaultMessage:
      'Bitte fülle die folgenden Felder aus, damit wir deine E-Mail ohne Verzögerung beantworten können:',
  },
  submit: {
    id: 'forms.contact.submit',
    defaultMessage: 'Meine Nachricht abschicken',
  },
  submitDisabled: {
    id: 'forms.contact.submit.disabled',
    defaultMessage: 'Bitte fülle zuerst das Formular aus',
  },
  sucessHeadline: {
    id: 'forms.contact.success.headline',
    defaultMessage: 'Danke für deine Nachricht',
  },
  cardHeadline: {
    id: 'forms.contact.cardheadline',
    defaultMessage: 'Kontaktformular',
  },
  sucessDescription: {
    id: 'forms.contact.success.description',
    defaultMessage:
      'Dein Kontaktformular wurde erfolgreich abgeschickt. Deine Anfrage wird schnellstmöglich von uns bearbeitet.',
  },
});
const buildYupSchemaWithIntlFormatMessage = (intl: IntlShape): ObjectSchema<FormValues> =>
  yup.object().shape({
    [InputIDs.CUSTOMERID]: yup
      .string()
      .matches(
        config.forms.validation.rules.customerId.regex,
        intl.formatMessage(config.forms.validation.rules.customerId.errorMessage),
      ),
    [InputIDs.FIRSTNAME]: yup
      .string()
      .matches(noSpecCharsNoNumbers, intl.formatMessage(specialErrorMessages.noSpecCharsNoNumbers))
      .required(intl.formatMessage(formMessages[InputIDs.FIRSTNAME].error)),
    [InputIDs.LASTNAME]: yup
      .string()
      .matches(noSpecCharsNoNumbers, intl.formatMessage(specialErrorMessages.noSpecCharsNoNumbers))
      .required(intl.formatMessage(formMessages[InputIDs.LASTNAME].error)),
    [InputIDs.STREET]: yup
      .string()
      .matches(noSpecCharsNoNumbers, intl.formatMessage(specialErrorMessages.noSpecCharsNoNumbers))
      .required(intl.formatMessage(formMessages[InputIDs.STREET].error)),
    [InputIDs.HOUSENUMBER]: yup
      .string()
      .matches(noSpecChars, intl.formatMessage(specialErrorMessages.noSpecChars))
      .required(intl.formatMessage(formMessages[InputIDs.HOUSENUMBER].error)),
    [InputIDs.POSTALCODE]: yup
      .string()
      .matches(numbersOnly, intl.formatMessage(specialErrorMessages.numbersOnly))
      .required(intl.formatMessage(formMessages[InputIDs.POSTALCODE].error)),
    [InputIDs.CITY]: yup
      .string()
      .matches(noSpecCharsNoNumbers, intl.formatMessage(specialErrorMessages.noSpecChars))
      .required(intl.formatMessage(formMessages[InputIDs.CITY].error)),
    [InputIDs.EMAIL]: yup
      .string()
      .email(intl.formatMessage(specialErrorMessages.email))
      .required(intl.formatMessage(formMessages[InputIDs.EMAIL].error)),
    [InputIDs.PHONENUMBER]: yup.string(),

    [InputIDs.SUBJECT]: yup
      .string()
      .required(intl.formatMessage(formMessages[InputIDs.SUBJECT].error)),
    [InputIDs.MESSAGE]: yup
      .string()
      .required(intl.formatMessage(formMessages[InputIDs.MESSAGE].error)),
  });
const getDefaultValues = (customer: CustomerDataWithStatus | undefined): FormValues => ({
  [`${InputIDs.FIRSTNAME}`]: customer?.firstName || '',
  [`${InputIDs.LASTNAME}`]: customer?.lastName || '',
  [`${InputIDs.STREET}`]: customer?.billingAddress?.street || '',
  [`${InputIDs.HOUSENUMBER}`]: customer?.billingAddress?.houseNumber || '',
  [`${InputIDs.CITY}`]: customer?.billingAddress?.city || '',
  [`${InputIDs.POSTALCODE}`]: customer?.billingAddress?.zipCode || '',
  [`${InputIDs.EMAIL}`]: customer?.email || '',
  [`${InputIDs.CUSTOMERID}`]: customer?.businessPartnerNo,
  // "find" first element in array. Can't use phoneNumbers[0, as array might be undefined
  [`${InputIDs.PHONENUMBER}`]: customer?.phoneNumbers?.find(Boolean)?.number,
});

export const convertFormDataToPostData = (
  formValues: FormValues,
): ConvertedFormMessageDataType => ({
  subject: formValues[InputIDs.SUBJECT] || '',
  message: formValues[InputIDs.MESSAGE] || '',
  customerData: {
    firstName: formValues[InputIDs.FIRSTNAME] || '',
    lastName: formValues[InputIDs.LASTNAME] || '',
    customerId: formValues[InputIDs.CUSTOMERID] || '',
    street: formValues[InputIDs.STREET] || '',
    housenumber: formValues[InputIDs.HOUSENUMBER] || '',
    city: formValues[InputIDs.CITY] || '',
    postalCode: formValues[InputIDs.POSTALCODE] || '',
    emailAddress: formValues[InputIDs.EMAIL] || '',
    phoneNumber: formValues[InputIDs.PHONENUMBER] || '',
  },
  messageType: 'CONTACT' as const,
});
const ContactForm = () => {
  const intl = useIntl();
  const dispatchGtmEvent = useTracking();
  useEffect(
    () => {
      dispatchGtmEvent<GTMEventGlycerinDisplayForm>({
        event: 'DisplayForm',
        DisplayFormData: { category: 'contact-form', label: 'customerservice', detail: 'open' },
      });
    },
    // INSPIRE-3537 - only fire once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { customer } = useCustomer();
  const [errorNotification, setErrorNotification] = useState({ show: false, message: '' });
  const { isMobile } = useDeviceType();

  const schema = buildYupSchemaWithIntlFormatMessage(intl);
  const { register, handleSubmit, formState, setError, control } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: getDefaultValues(customer),
  });
  const { isSubmitting, isValid, errors, isSubmitSuccessful } = formState;
  const headingArea = {
    headline: intl.formatMessage(contactMessages.headline),
    description: intl.formatMessage(contactMessages.description),
  };
  const formHeading = intl.formatMessage(contactMessages.cardHeadline);
  const successPage = {
    headline: intl.formatMessage(contactMessages.sucessHeadline),
    description: intl.formatMessage(contactMessages.sucessDescription),
  };
  const formDeviceType = useFormDeviceType();
  const onSubmit = async (data: FormValues) => {
    await submitMessage<SetErrorType>({
      intl,
      formDeviceType,
      convertedFormMessageData: convertFormDataToPostData(data),
      setError,
      endpoint: 'message',
      onSuccess: () => {
        dispatchGtmEvent<GTMEventGlycerinFormSubmitClick>({
          event: 'FormSubmitClick',
          FormSubmitClickData: {
            category: 'contact-form',
            label: 'customerservice',
            detail: 'send',
          },
        });
      },
      onError: (errorMsg) => {
        setErrorNotification({ show: true, message: errorMsg });
        dispatchGtmEvent<GTMEventGlycerinDisplayError>({
          event: 'DisplayError',
          DisplayErrorData: {
            category: 'contact-form',
            label: '?praefix Formularfeld?',
            detail: errorMsg,
            errors: [
              {
                errorType: 'formValidation',
                message: errorMsg,
                severity: GlycerinErrorSeverity.CRITICAL,
                source: 'contact-form',
              },
            ],
          },
        });
      },
    });
  };
  const submitButton = {
    enabled: intl.formatMessage(contactMessages.submit),
    disabled: intl.formatMessage(contactMessages.submitDisabled),
  };
  const getFieldProps = (inputId: InputId) => ({
    label: intl.formatMessage(formMessages[inputId].label),
    ...register(inputId),
    helperText: errors[inputId]?.message, // is provided by the yup schema
    error: !!errors[inputId],
    placeholder:
      formMessages[inputId].placeholder.defaultMessage !== ''
        ? intl.formatMessage(formMessages[inputId].placeholder)
        : undefined,
    fullWidth: true,
    required: !(schema.describe().fields[inputId] as SchemaDescription).optional,
  });
  if (isSubmitSuccessful) {
    return <SuccessPage {...successPage} />;
  }
  const handler = handleSubmit(onSubmit);
  return (
    <GeneralFormWrapper
      isSubmitting={isSubmitting}
      isValid={isValid}
      submitButton={submitButton}
      submitHandler={handler}
      headingArea={headingArea}
      formHeading={formHeading}
      handleOnClose={() => setErrorNotification({ ...errorNotification, show: false })}
      errorNotification={errorNotification}
    >
      <Grid container columns={[1, 1, 2]} sx={{ marginBottom: 3 }} columnSpacing={4} rowSpacing={3}>
        <Grid item xs={1} md={1}>
          <TextField {...getFieldProps(`${InputIDs.CUSTOMERID}`)} />
        </Grid>
        {!isMobile && <Grid item xs={1} md={1} />}
        <Grid item xs={1}>
          <TextField {...getFieldProps(`${InputIDs.FIRSTNAME}`)} />
        </Grid>
        <Grid item xs={1}>
          <TextField {...getFieldProps(`${InputIDs.LASTNAME}`)} />
        </Grid>
        <Grid item xs={1}>
          <Grid container columnSpacing={1.5} rowSpacing={1.5}>
            <Grid item xs>
              <TextField {...getFieldProps(`${InputIDs.STREET}`)} />
            </Grid>
            <Grid item sx={{ width: '7rem' }}>
              <TextField {...getFieldProps(`${InputIDs.HOUSENUMBER}`)} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={1}>
          <Grid container columnSpacing={1.5} rowSpacing={1.5}>
            <Grid item sx={{ width: '9rem' }}>
              <TextField {...getFieldProps(`${InputIDs.POSTALCODE}`)} type="tel" />
            </Grid>
            <Grid item xs>
              <TextField {...getFieldProps(`${InputIDs.CITY}`)} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={1}>
          <TextField {...getFieldProps(`${InputIDs.EMAIL}`)} />
        </Grid>
        <Grid item xs={1} data-testid="">
          <TextField {...getFieldProps(`${InputIDs.PHONENUMBER}`)} />
        </Grid>

        <Grid item xs={1}>
          <Select
            {...get(InputIDs.SUBJECT)}
            items={createFormSubSelectionsSelect(config.forms.contact.selectOptionsSubject, intl)}
            controlProps={{ fullWidth: true }}
            control={control}
            controllerName={InputIDs.SUBJECT}
            required={!(schema.describe().fields[InputIDs.SUBJECT] as SchemaDescription).optional}
            label={`${intl.formatMessage(formMessages[InputIDs.SUBJECT].label)} *`}
          />
        </Grid>
      </Grid>
      <TextField
        {...getFieldProps(InputIDs.MESSAGE)}
        type="textarea"
        multiline
        minRows={4}
        maxRows={8}
      />
    </GeneralFormWrapper>
  );
};

// eslint-disable-next-line import/no-default-export
export default ContactForm;
